/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useState, useEffect, FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import axios from "axios";

import { Organization } from "../../../types/organizations";
import { apiClient } from "../../../api/apiClient";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../../layout";

export const defaultOrganization: Partial<Organization> = {
  id: "",
  name: "",
  short_description: "",
  description: "",
  user_count: "",
  slack_webhook_url: "",
  enforce_2fa: false,
  slack_enabled: false,
  org_admins_can_manage_er_users: false,
};

interface EditOrganizationModalProps {
  organization?: Partial<Organization> | null;
  isOpen?: boolean;
  mode?: "modal" | "page";
  onClose?: () => void;
}

const AdminEditOrganizationModal: FunctionComponent<
  EditOrganizationModalProps
> = ({
  organization = defaultOrganization,
  isOpen = true,
  mode = "modal",
  onClose = () => {},
}) => {
  const navigate = useNavigate();
  const [editedOrganization, setEditedOrganization] = useState<
    Organization | Partial<Organization>
  >(organization || defaultOrganization);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPageMode = mode === "page";

  useEffect(() => {
    if (organization) {
      setEditedOrganization(organization);
    } else {
      setEditedOrganization(defaultOrganization);
    }
  }, [organization]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if ("id" in editedOrganization && editedOrganization.id) {
        await apiClient.patch(
          `/api/organizations/${editedOrganization.id}/`,
          editedOrganization
        );
        toast.success(
          `Organization ${editedOrganization.name} updated successfully`
        );
      } else {
        await apiClient.post("/api/organizations/", editedOrganization);
        toast.success(
          `Organization ${editedOrganization.name} created successfully`
        );
      }
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorData = error.response.data;
        for (const key in errorData) {
          if (Array.isArray(errorData[key])) {
            const errorMessage = errorData[key].join(", ");
            toast.error(`${key.toUpperCase()}: ${errorMessage}`);
          } else {
            toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
          }
        }
      } else {
        toast.error(`Error saving organization: ${error}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Organization?"
    );
    if (confirmDelete && editedOrganization.id) {
      try {
        await apiClient.delete(`/api/organizations/${editedOrganization.id}`);
        toast.success("Organization successfully deleted");
        navigate("/organizations");
      } catch (error) {
        toast.error("Failed to delete the organization");
      }
    }
  };

  const content = (
    <>
      <DialogContent>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            value={editedOrganization.name}
            disabled={editedOrganization.id ? true : false}
            onChange={(e) =>
              setEditedOrganization({
                ...editedOrganization,
                name: e.target.value,
              })
            }
            margin="normal"
            required
          />
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            fullWidth
            label="Short Description"
            variant="outlined"
            value={editedOrganization.short_description}
            onChange={(e) =>
              setEditedOrganization({
                ...editedOrganization,
                short_description: e.target.value,
              })
            }
            margin="normal"
            multiline
            rows={2}
          />
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            value={editedOrganization.description}
            onChange={(e) =>
              setEditedOrganization({
                ...editedOrganization,
                description: e.target.value,
              })
            }
            margin="normal"
            multiline
            rows={4}
          />
        </Box>

        {editedOrganization.id && (
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              padding: 2,
              marginTop: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              fullWidth
              label="User Count"
              variant="outlined"
              value={editedOrganization.user_count}
              disabled={true}
              margin="normal"
            />
          </Box>
        )}

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            fullWidth
            label="Slack Webhook URL"
            variant="outlined"
            value={editedOrganization.slack_webhook_url}
            onChange={(e) =>
              setEditedOrganization({
                ...editedOrganization,
                slack_webhook_url: e.target.value,
              })
            }
            margin="normal"
            placeholder="https://hooks.slack.com/services/..."
          />
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={editedOrganization.enforce_2fa}
                onChange={(e) =>
                  setEditedOrganization({
                    ...editedOrganization,
                    enforce_2fa: e.target.checked,
                  })
                }
                name="enforce_2fa"
              />
            }
            label="Enforce 2FA"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, all users in this organization will be required to set
            up two-factor authentication.
          </Typography>
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={editedOrganization.slack_enabled}
                onChange={(e) =>
                  setEditedOrganization({
                    ...editedOrganization,
                    slack_enabled: e.target.checked,
                  })
                }
                name="slack_enabled"
              />
            }
            label="Slack Notifications Enabled"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enable Slack notifications for this organization. Requires a valid
            webhook URL.
          </Typography>
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            padding: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={editedOrganization.org_admins_can_manage_er_users || false}
                onChange={(e) =>
                  setEditedOrganization({
                    ...editedOrganization,
                    org_admins_can_manage_er_users: e.target.checked,
                  })
                }
                name="org_admins_can_manage_er_users"
              />
            }
            label="Organization Admins Can Manage ER Users"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, organization administrators can manage evidence request users.
          </Typography>
        </Box>
      </DialogContent>
    </>
  );

  return isPageMode ? (
    <Layout
      pageTitle="Organization"
      parentUrl="/organizations"
      parentPageName="Organizations"
    >
      <Box>
        <Box sx={{ paddingRight: "6vh" }}>
          <Typography fontSize={25} sx={{ color: "black", padding: 3 }}>
            {editedOrganization.name || "New Organization"}
          </Typography>

          <DialogActions>
            {editedOrganization.id && (
              <Button variant="contained" color="error" onClick={triggerDelete}>
                Delete Organization
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {editedOrganization.id
                ? "Update Organization"
                : "Create Organization"}
            </Button>
          </DialogActions>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              color: "black",
              border: 2,
              borderColor: "divider",
              margin: 2,
              borderRadius: 3,
            }}
          >
            {content}
          </Box>
        </Box>
      </Box>
    </Layout>
  ) : (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontSize={25}>
          {editedOrganization.name || "New Organization"}
        </Typography>
        <CloseIcon
          onClick={onClose}
          style={{
            position: "absolute",
            right: 10,
            top: 10,
            fontSize: 30,
            cursor: "pointer",
          }}
        />
      </DialogTitle>

      {content}

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {editedOrganization.id ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminEditOrganizationModal;
